import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTable, Column, Cell, HeaderGroup, Row } from 'react-table'

import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Paper from '@mui/material/Paper'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded'
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded'
import StarRoundedIcon from '@mui/icons-material/StarRounded'

import ChipButton from '@/components/ui/buttons/ChipButton'
import DelayedRender from '@/components/hoc/DelayedRender'
import Pagination from '@/components/ui/pagination/Pagination'

export type MeetingLinkData = {
  id: string
  isPrioritized?: boolean
  name: string
}

type MeetingLinksTableProps = {
  data: MeetingLinkData[]
  loading?: boolean
  onPageChange?: (newPage: number, rowsPerPage: number) => void
  onSelect?: (meetingLinkId: string) => void
  rowsPerPageOptions?: number[]
  totalCount: number
}

const defaultRowsPerPageOptions = [5, 10, 15]

export function MeetingLinksTable(props: MeetingLinksTableProps) {
  const { data, loading = false, rowsPerPageOptions = defaultRowsPerPageOptions, onPageChange, onSelect, totalCount } = props
  const { t } = useTranslation()

  const [page, setPage] = useState<number>(1)
  const [rowsPerPage, setRowsPerPage] = React.useState(rowsPerPageOptions[0])

  const columns = useMemo(() => {
    return [
      {
        Header: t('page.meetingLinks.meetingLinksTable.header.name'),
        accessor: 'name',
        width: 'unset',
      },
      {
        Header: t('page.meetingLinks.meetingLinksTable.header.isPrioritized'),
        accessor: 'isPrioritized',
        Cell: ({ value }) => <Stack alignItems="center">{value ? <StarRoundedIcon color="primary" /> : <StarBorderRoundedIcon />}</Stack>,
        align: 'center',
        width: 'unset',
      },
    ] as Column<MeetingLinkData>[]
  }, [t])

  const { getTableBodyProps, headerGroups, prepareRow, rows } = useTable<MeetingLinkData>({
    columns,
    data,
  })

  const emptyRows = Math.max(0, rowsPerPage - rows.length)

  const handleChangePage = (newPage: number, rowsPerPage: number) => {
    if (page === newPage) return
    setPage(newPage)
    onPageChange?.(newPage, rowsPerPage)
  }

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage)
    if (Math.ceil(totalCount / newRowsPerPage) >= page) onPageChange?.(page, newRowsPerPage)
  }

  const handleSelectMeetingLink = (row: Row<MeetingLinkData>) => () => {
    onSelect?.(row.original.id)
  }

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }}>
        <TableHead>
          {headerGroups.map((headerGroup: HeaderGroup<MeetingLinkData>) => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column: HeaderGroup<MeetingLinkData>) => {
                return (
                  <TableCell
                    {...column.getHeaderProps()}
                    align={column.align || 'left'}
                    scope="col"
                    sx={{ width: column.width, maxWidth: column.maxWidth, minWidth: column.minWidth, wordWrap: 'break-word' }}
                  >
                    {column.render('Header')}
                  </TableCell>
                )
              })}
              <TableCell align="right" sx={{ p: 0, pr: 2 }}>
                <IconButton aria-label="configure columns" size="small">
                  <SettingsRoundedIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableHead>
        <TableBody {...getTableBodyProps()} sx={{ position: 'relative' }}>
          {loading && (
            <DelayedRender delay={300}>
              <TableRow>
                <TableCell sx={{ border: 'none', padding: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: 'rgba(255, 255, 255, 0.7)',
                    }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                </TableCell>
              </TableRow>
            </DelayedRender>
          )}
          {rows.map((row) => {
            prepareRow(row)
            return (
              <TableRow {...row.getRowProps()} key={row.original.id}>
                {row.cells.map((cell: Cell<MeetingLinkData>) => (
                  <TableCell {...cell.getCellProps()} align={cell.column.align || 'left'}>
                    {cell.render('Cell')}
                  </TableCell>
                ))}
                <TableCell align="right">
                  <Stack justifyContent="flex-end" direction="row" spacing={1}>
                    <Tooltip title={t('page.waitingRooms.waitingRoomsTable.openWaitingRoomButton.tooltip')}>
                      <ChipButton color="info" onClick={handleSelectMeetingLink(row)}>
                        <EditRoundedIcon />
                      </ChipButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            )
          })}
          {emptyRows > 0 && (
            <TableRow style={{ height: 63 * emptyRows }}>
              <TableCell colSpan={columns.length + 1} />
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Box sx={{ display: 'flex', justifyContent: 'end', p: '10px' }}>
        <Pagination
          color="primary"
          itemsPerPageOptions={rowsPerPageOptions}
          shape="rounded"
          page={page}
          onPageChange={handleChangePage}
          onItemsPerPageChange={handleRowsPerPageChange}
          totalItems={totalCount}
        />
      </Box>
    </TableContainer>
  )
}

export default MeetingLinksTable
